import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileExportOptions } from '../../../../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../../../../services/axios/skymap-axios-service-factory';
import { WebsocketManager } from '../../../../../../utils/websocket/websocket-manager';
import { Design } from '../../../../../../viewer/elements/design';
import { publish, SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { DesignTool } from '../../../../../../viewer/tools/design-tool';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useSharedView } from '../../../../../hooks/use-shared-view';
import { sanitizeFileBaseName } from '../../../../../utils/file';
import { getFileBaseName } from '../../../../../utils/path';
import { ExportAssetDialog } from '../../../../skyview/dialogs/export-asset-dialog/export-asset-dialog';
import { MoveIcon } from '../../../../skyview/right-menu/components/move-icon';
import { useSubscribeToExportCreatedMessage } from '../../../../skyview/right-menu/tools/design-tool-menu/features/import-geometry';
import { SkyviewContext } from '../../../../skyview/skyview-state';
import { useSubscribeShowDeleteAssetDialog } from '../../../hooks/subscribe-show-delete-asset-dialog';
import { AssetHeader } from '../../components/asset-blocks/asset-header';
import { DeleteAssetDialog } from '../../dialogs/delete-asset-dialog';
import { DisplaySettingsDialog, DisplaySettingsIcon } from '../../dialogs/display-settings-dialog';
import { DuplicateAssetDialog } from '../../dialogs/duplicate-asset-dialog';
import { EditAssetNameDialog } from '../../dialogs/edit-asset-name-dialog';
import { DeleteIcon } from '../../icons/delete-icon';
import { DownloadIcon } from '../../icons/download-icon';
import { DuplicateIcon } from '../../icons/duplicate-icon';
import { EditIcon } from '../../icons/edit-icon';

type Props = {
  asset: Design;
};

const DesignHeader = (props: Props) => {
  const { t } = useTranslation();
  const displaySettingsDialog = useDialog();
  const exportAssetDialog = useDialog();
  const editNameDialog = useDialog();
  const duplicateAssetDialog = useDialog();
  const deleteDialog = useDialog();
  const { isSharedView } = useSharedView();
  const { activeTool } = React.useContext(SkyviewContext);
  const assetLoadedAndVisible =
    props.asset.getStateVisibility() && props.asset.loadingStatus === 'loaded';
  useSubscribeShowDeleteAssetDialog(props.asset, deleteDialog, isSharedView);
  const [exportFileName, setExportFileName] = useState<string>(props.asset.uuid);

  useSubscribeToExportCreatedMessage(exportFileName, (event) => {
    if (event.status === 'COMPLETED' && !event.resultSaved) {
      window.location.assign(event.downloadUrl);
    }
  });

  const isBeingEdited = activeTool instanceof DesignTool && activeTool.asset === props.asset;

  return (
    <>
      <AssetHeader
        additionalIcons={
          isSharedView
            ? undefined
            : [
                <DisplaySettingsIcon
                  disabled={!assetLoadedAndVisible}
                  key="displaySettings"
                  onClick={(e) => displaySettingsDialog.show()}
                />,
                <EditIcon
                  disabled={isBeingEdited}
                  key="editIcon"
                  onClick={(e) => editNameDialog.show({ x: e.clientX, y: e.clientY })}
                />,
                <MoveIcon
                  asset={props.asset}
                  disabled={isBeingEdited || !assetLoadedAndVisible}
                  key="moveIcon"
                />,
                <DuplicateIcon
                  disabled={isBeingEdited}
                  key="duplicateIcon"
                  onClick={(e) => duplicateAssetDialog.show({ x: e.clientX, y: e.clientY })}
                />,
                <DeleteIcon
                  disabled={isBeingEdited}
                  key="deleteIcon"
                  onClick={(e) => deleteDialog.show({ x: e.clientX, y: e.clientY })}
                />,
              ]
        }
        asset={props.asset}
        icons={
          isSharedView
            ? undefined
            : [
                <DownloadIcon
                  disabled={isBeingEdited}
                  key="downloadIcon"
                  title={t('design.icons.download', { ns: 'skyviewAssetMenu' })}
                  onClick={() => {
                    if (props.asset instanceof Design) {
                      exportAssetDialog.show();
                    }
                    return Promise.resolve();
                  }}
                />,
              ]
        }
        name={props.asset.name}
      />

      {exportAssetDialog.render(
        //
        // As this dialog also is used outside the translation ticket (the old SkyView) we need
        // to disable google translate via a prop for the new SkyView.
        //
        // todo: When this component is only used in the new SkyView we can remove this prop.
        //
        <ExportAssetDialog
          asset={props.asset}
          onClose={() => {
            exportAssetDialog.hide();
          }}
          onDirectDownload={async ({
            format,
            newLayerType,
            removeFolderTags,
            saveToFileManager,
            folderId,
          }) => {
            exportAssetDialog.hide();

            if (format === '.kml' || format === '.shp' || format === '.geojson') {
              try {
                await WebsocketManager.instance.waitForWebsocketConnection();
                publish(SubscriptionTopic.ToastrMessage, {
                  type: 'info',
                  message: t('design.exportDialog.exportPending', { ns: 'skyviewAssetMenu' }),
                });

                const fileName = `${sanitizeFileBaseName(getFileBaseName(props.asset.name))}${format}`;
                setExportFileName(fileName);

                //
                // removeFolderTags is only part of the KML options, so the object is created
                // per format in order to keep it assignable to FileExportOptions.
                //
                const options: FileExportOptions =
                  format === '.kml'
                    ? {
                        type: format,
                        newLayerType: newLayerType ?? 'POLYGON',
                        removeFolderTags,
                      }
                    : { type: format, newLayerType: newLayerType ?? 'POLYGON' };

                await SkyMapAxiosServiceFactory.instance.createAssetServiceV1().createExport({
                  path: { dynamicAssetId: props.asset.assetId ?? '' },
                  body: {
                    fileName,
                    saveResult: saveToFileManager,
                    options,
                    folderId,
                  },
                });
              } catch {
                publish(SubscriptionTopic.ToastrMessage, {
                  type: 'error',
                  message: t('unexpectedErrorWithSupport', { ns: 'common' }),
                });
              }

              return;
            }

            const result = await SkyMapAxiosServiceFactory.instance
              .createAssetServiceV1()
              .getExport({
                path: { dynamicAssetId: props.asset.assetId! },
                query: { format },
              });

            window.location.assign(result.data.url);
          }}
        />,
      )}

      {editNameDialog.render((pos) => (
        <EditAssetNameDialog asset={props.asset} pos={pos} onClose={editNameDialog.hide} />
      ))}

      {duplicateAssetDialog.render((pos) => (
        <DuplicateAssetDialog asset={props.asset} pos={pos} onClose={duplicateAssetDialog.hide} />
      ))}

      {deleteDialog.render((pos) => (
        <DeleteAssetDialog asset={props.asset} pos={pos} onClose={deleteDialog.hide} />
      ))}
      {displaySettingsDialog.render(
        <DisplaySettingsDialog asset={props.asset} dialog={displaySettingsDialog} />,
      )}
    </>
  );
};

export { DesignHeader, Props as DesignHeaderProps };
